import { useState } from 'react';
import { Camera, ChevronRight, Inbox, RotateCcw, TriangleAlert, X } from 'lucide-react';
import type { WriteOffListRow } from '@eombor/contracts';
import { Badge, Button, Card, EmptyState, Skeleton, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { useMyWriteOffs, useReviewWriteOff } from '../model/write-off.queries.js';
import { WriteOffActDialog } from './WriteOffActDialog.js';
import { WriteOffStatusBadge } from './WriteOffStatusBadge.js';

const qtyText = (qty: number): string => (Math.round(qty * 1000) / 1000).toLocaleString('ru-RU');

const pad = (value: number): string => String(value).padStart(2, '0');

/** «01.09.2026» — kunning o'zi yetarli, soat kartochkani shovqinlantirardi. */
const formatDay = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
};

/**
 * «MENING RASXODLARIM» — xodimning o'z aktlari, TALABNOMA bo'yicha
 * guruhlangan.
 *
 * Guruhlash qoldiq ro'yxatidagi bilan ayni sabab: bitta qog'oz bilan
 * uch dona kelgan bo'lsa, ularning rasxodi ham bitta qog'ozga tegishli
 * va ekranda yonma-yon turishi kerak.
 */
export const MyWriteOffsView = () => {
  const acts = useMyWriteOffs({ limit: 100 });
  const [openActId, setOpenActId] = useState<string | null>(null);

  if (acts.isPending) return <Skeleton className="h-64 w-full" />;

  if (acts.isError) {
    return (
      <Card>
        <EmptyState
          icon={<TriangleAlert size={20} aria-hidden />}
          title={t('writeOff.loadError')}
          description={acts.error.message}
        />
      </Card>
    );
  }

  if (acts.data.items.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Inbox size={20} aria-hidden />}
          title={t('myStock.writeOffEmpty')}
          description={t('myStock.writeOffEmptyHint')}
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {groupByRequisition(acts.data.items).map((group) => (
        <section
          key={group.key}
          aria-label={group.label}
          className={cn(
            'flex flex-col gap-3 rounded-[var(--radius-lg)] border border-border',
            'bg-surface-2 p-3 sm:p-4',
          )}
        >
          <header className="flex flex-wrap items-center justify-end gap-2">
            <Badge tone="neutral" mono>
              {group.label}
            </Badge>
          </header>

          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {group.items.map((row) => (
              <MyWriteOffCard key={row.id} row={row} onOpen={() => setOpenActId(row.id)} />
            ))}
          </ul>
        </section>
      ))}

      {openActId === null ? null : (
        <WriteOffActDialog actId={openActId} mode="owner" onClose={() => setOpenActId(null)} />
      )}
    </div>
  );
};

interface Group {
  key: string;
  label: string;
  items: WriteOffListRow[];
}

/**
 * Aktlarni TALABNOMA bo'yicha yig'adi — qoldiqdagi `groupByRequisition`
 * bilan ayni qoida.
 *
 * Talabnomasiz aktlar bitta umumiy guruhga tushadi: har biri o'ziga
 * alohida quti bo'lsa, ekranda bir nechta bo'sh sarlavhali quti paydo
 * bo'lardi.
 */
const groupByRequisition = (rows: readonly WriteOffListRow[]): Group[] => {
  const groups: Group[] = [];
  const byKey = new Map<string, Group>();

  for (const row of rows) {
    const key = row.requisitionDocNo ?? 'no-requisition';
    const existing = byKey.get(key);
    if (existing !== undefined) {
      existing.items.push(row);
      continue;
    }
    const group: Group = {
      key,
      label: row.requisitionDocNo ?? t('myStock.tabWriteOffs'),
      items: [row],
    };
    byKey.set(key, group);
    groups.push(group);
  }

  return groups;
};

/**
 * Aktning kartochkasi — xodim tomonidan ko'rinishi.
 *
 * Buxgalternikidan farqi harakatlarda: bu yerda tasdiqlash yo'q, lekin
 * RAD ETILGANINI tuzatib qayta yuborish va kutilayotganini bekor qilish
 * bor. Rad etish sababi kartochkaning O'ZIDA turadi — xodim nima
 * tuzatish kerakligini boshqa ekranga o'tmasdan ko'rishi kerak.
 */
const MyWriteOffCard = ({ row, onOpen }: { row: WriteOffListRow; onOpen: () => void }) => {
  const review = useReviewWriteOff();
  const [busy, setBusy] = useState<'resubmit' | 'cancel' | null>(null);

  const run = (action: 'resubmit' | 'cancel'): void => {
    setBusy(action);
    review.mutate({ id: row.id, action }, { onSettled: () => setBusy(null) });
  };

  return (
    <li
      className={cn(
        'flex flex-col gap-3 rounded-[var(--radius-md)] border border-border',
        'bg-surface p-4 shadow-[var(--shadow-1)]',
      )}
    >
      {/* Kartochkaning O'ZI ochiladi: akt qog'ozi, suratlar, xarita va
          «kim tasdiqladi» shu yerda emas, oynada turadi — kartochka esa
          ro'yxatda o'nlab bo'lishi mumkin. */}
      <button type="button" onClick={onOpen} className="flex flex-col gap-3 text-left">
        <span className="flex w-full flex-wrap items-center justify-between gap-2">
          <span className="tabular text-sm font-semibold text-text">{row.docNo}</span>
          <WriteOffStatusBadge status={row.status} />
        </span>

        <span className="min-w-0 text-sm leading-5 font-medium text-pretty text-text">
          {row.nomenclatureName}
        </span>

        <span className="flex items-baseline gap-1.5">
          <span className="tabular text-2xl leading-7 font-semibold text-text">
            {qtyText(row.qty)}
          </span>
          <span className="text-sm text-text-muted">{row.unit}</span>
        </span>

        {/* Pastki qator kartochkaning BOSILISHINI ham aytadi: strelkasiz
            kartochka oddiy matn bo'lib ko'rinadi va odam uni ochib
            ko'rmasdi. */}
        <span className="flex w-full items-center gap-3 text-xs text-text-muted">
          <span className="flex items-center gap-1.5">
            <Camera size={12} aria-hidden />
            <span className="tabular">{row.photoCount}</span>
          </span>
          <span className="tabular">{formatDay(row.submittedAt)}</span>
          <span className="ml-auto flex items-center gap-1 text-text-faint">
            {t('writeOff.details')}
            <ChevronRight size={12} aria-hidden />
          </span>
        </span>
      </button>

      {/* RAD ETISH SABABI kartochkaning o'zida: xodim nima tuzatish
          kerakligini boshqa ekranga o'tmasdan ko'rishi kerak. */}
      {row.status === 'REJECTED' && row.lastRejectReason !== null ? (
        <p className="border-t border-border pt-2 text-xs text-danger">
          {row.lastRejectReason}
        </p>
      ) : null}

      {row.status === 'PENDING' || row.status === 'REJECTED' ? (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-2.5">
          {row.status === 'REJECTED' ? (
            <Button
              variant="ghost"
              size="sm"
              loading={busy === 'resubmit'}
              onClick={() => run('resubmit')}
            >
              <RotateCcw size={14} aria-hidden />
              {t('writeOff.resubmit')}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            loading={busy === 'cancel'}
            onClick={() => run('cancel')}
          >
            <X size={14} aria-hidden />
            {t('writeOff.cancel')}
          </Button>
        </div>
      ) : null}
    </li>
  );
};
